import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import UnionIcon from '@/assets/icons/union.svg?react'
import Button from '@/components/Button/Button'
import TextWithLabel from '@/components/TextWithLabel/TextWithLabel'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'
import { formatDate } from '@/utils/format-date'

import styles from './CurriculumManagementDetailsView.module.scss'
import BasketSummaryTable from '../components/BasketSummaryTable'
import CurriculumLayoutHeader from '../components/CurriculumLayoutHeader'
import RemoveCurriculumModal from '../components/RemoveCurriculumModal'
import useDuplicateCurriculum from '../mutations/useDuplicateCurriculum'
import { useCurriculumDetails } from '../queries/curriculumDetailsQueryOptions'
import type { SimpleCurriculum } from '../types/simple-curriculum'

const routeApi = getRouteApi('/_auth/curricula/$curriculumId/')

const CurriculumManagementDetailsView = () => {
  const { t } = useTranslation('curriculumsManagement')

  const [removeCurriculumModal, setRemoveCurriculumModal] =
    useState<SimpleCurriculum | null>(null)

  const navigate = routeApi.useNavigate()
  const curriculumId = routeApi.useParams().curriculumId
  const { data: curriculum } = useCurriculumDetails(curriculumId)

  const { mutate: duplicateCurriculumMutation } = useDuplicateCurriculum({
    onSuccess: ({ newCurriculumId }) => {
      toast({
        title: t('toast.curriculum-duplicated'),
        withClose: true,
        action: {
          render: (
            <Button
              variant="info"
              size="small"
              onClick={() => {
                navigate({
                  to: '/curricula/$curriculumId',
                  params: {
                    curriculumId: newCurriculumId
                  }
                })
              }}
            >
              {t('button.view')}
            </Button>
          ),
          altText: t('button.view')
        },
        variant: 'info'
      })
    },
    onError: () => {
      toast({
        title: t('toast.failed-to-duplicate-curriculum'),
        variant: 'error'
      })
    }
  })

  if (!curriculum) return null

  const duplicateCurriculum = () =>
    duplicateCurriculumMutation({ curriculumId: curriculum.id })

  const isEditingEnabled = curriculum.canBeEdited

  const actions: Action[] = [
    {
      text: t('button.edit'),
      icon: <EditIcon />,
      asLink: true,
      to: '/curricula/$curriculumId/edit',
      params: {
        curriculumId: curriculum.id
      },
      hidden: !isEditingEnabled
    },
    {
      text: t('button.duplicate'),
      icon: <UnionIcon />,
      onClick: duplicateCurriculum
    },
    {
      text: t('button.remove'),
      icon: <DeleteIcon />,
      variantAction: 'danger',
      onClick: () => {
        if (!curriculum.canBeDeleted) {
          toast({
            variant: 'error',
            title: t('toast.cannot-remove.title', {
              NAME: curriculum.name
            }),
            description: t('toast.cannot-remove.description')
          })
          return
        }

        setRemoveCurriculumModal(curriculum)
      }
    }
  ]

  const infoArray = [
    {
      label: t('label.education-level'),
      value: curriculum.educationLevel.name
    },
    {
      label: t('label.created'),
      value: formatDate(curriculum.created)
    },
    {
      label: t('label.aim'),
      value: t(`aim.${curriculum.aim}`)
    }
  ]

  const handleRemoveSuccess = () => {
    navigate({
      to: '/curricula',
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }
  const closeRemoveCurriculumModal = () => setRemoveCurriculumModal(null)

  return (
    <>
      <BasicLayout
        header={
          <CurriculumLayoutHeader
            title={curriculum.name}
            status={curriculum.status}
          />
        }
        subheader={t('header.curriculum-details')}
        actionsAsDropdown
        actions={actions}
        breadcrumbs={[
          {
            title: t('header.curricula'),
            to: '/curricula'
          },
          {
            title: curriculum.name,
            to: '/curricula/$curriculumId',
            params: {
              curriculumId: curriculum.id
            }
          }
        ]}
        moduleName={t('header.administration', { ns: 'common' })}
      >
        <div className={styles.container}>
          <div className={styles.basicInformation}>
            <div className={styles.basicInformationHeader}>
              <h2 className={styles.heading}>
                {t('header.basic-information')}
              </h2>
              {isEditingEnabled ? (
                <Button
                  variant="secondary"
                  icon={<EditIcon />}
                  asLink
                  to="/curricula/$curriculumId/edit"
                  params={{
                    curriculumId: curriculum.id
                  }}
                >
                  {t('button.edit')}
                </Button>
              ) : null}
            </div>
            <div className={styles.basicInformationContent}>
              {infoArray.map((item, index) => (
                <TextWithLabel
                  label={item.label}
                  text={item.value}
                  key={index}
                  variant="horizontal-full-row"
                  size="small"
                />
              ))}
            </div>
          </div>
          {curriculum.baskets.map(basket => (
            <BasketSummaryTable
              key={basket.id}
              id={basket.id}
              title={t(`header.${basket.basketType}-basket`)}
              shouldLinkToCourse
              data={basket.basketCourses.map(basketCourse => ({
                course: basketCourse.course
                  ? {
                      id: basketCourse.course.id,
                      name: basketCourse.course.displayName
                    }
                  : null,
                timeUnit: basketCourse.timeUnit,
                semesters: basketCourse.semesters.map(
                  semester => semester.hours
                ),
                total: basketCourse.total
              }))}
            />
          ))}
        </div>
      </BasicLayout>
      {removeCurriculumModal ? (
        <RemoveCurriculumModal
          curriculum={removeCurriculumModal}
          onClose={closeRemoveCurriculumModal}
          onRemoveSuccess={handleRemoveSuccess}
        />
      ) : null}
    </>
  )
}

export default CurriculumManagementDetailsView
